import { useState, useEffect, useRef } from 'react';
import { X, Send, ThumbsUp, ThumbsDown, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  was_helpful?: boolean | null;
  created_at: string;
}

interface ChatWindowProps {
  onClose: () => void;
}

export function ChatWindow({ onClose }: ChatWindowProps) {
  const { user, currentTenant } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const suggestedQuestions = [
    "What features does FoodOps offer?",
    "How do I import sales data?",
    "How do I manage recipes?",
    "What integrations are available?",
  ];

  useEffect(() => {
    if (user && currentTenant) {
      loadOrCreateConversation();
    }
  }, [user, currentTenant]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadOrCreateConversation = async () => {
    if (!user || !currentTenant) return;

    const { data: existingConversation } = await supabase
      .from('chat_conversations')
      .select('id')
      .eq('user_id', user.id)
      .eq('tenant_id', currentTenant)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (existingConversation) {
      setConversationId(existingConversation.id);
      await loadMessages(existingConversation.id);
    } else {
      const { data: newConversation, error } = await supabase
        .from('chat_conversations')
        .insert({
          user_id: user.id,
          tenant_id: currentTenant,
          status: 'active',
        })
        .select()
        .single();

      if (error) {
        setError('Failed to start conversation. Please try again.');
        return;
      }

      setConversationId(newConversation.id);

      const welcomeMessage = {
        conversation_id: newConversation.id,
        role: 'assistant',
        content: 'Hello! I\'m here to help you with any questions about FoodOps. You can ask me about features, pricing, account management, or how to use specific functionality. How can I assist you today?',
      };

      await supabase.from('chat_messages').insert(welcomeMessage);
      setMessages([{ ...welcomeMessage, id: 'welcome', created_at: new Date().toISOString() }]);
    }
  };

  const loadMessages = async (convId: string) => {
    const { data, error } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('conversation_id', convId)
      .order('created_at', { ascending: true });

    if (error) {
      setError('Failed to load conversation history.');
      return;
    }

    setMessages(data || []);
  };

  const handleSend = async () => {
    if (!input.trim() || !conversationId || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setIsLoading(true);
    setError(null);

    const userMsg = {
      conversation_id: conversationId,
      role: 'user' as const,
      content: userMessage,
    };

    await supabase.from('chat_messages').insert(userMsg);
    setMessages(prev => [...prev, { ...userMsg, id: Date.now().toString(), created_at: new Date().toISOString() }]);

    try {
      const { data, error } = await supabase.functions.invoke('chat-support', {
        body: {
          message: userMessage,
          conversation_id: conversationId,
          tenant_id: currentTenant,
        },
      });

      if (error) throw error;

      const assistantMsg = {
        conversation_id: conversationId,
        role: 'assistant' as const,
        content: data.response,
        metadata: data.metadata || {},
      };

      await supabase.from('chat_messages').insert(assistantMsg);
      setMessages(prev => [...prev, { ...assistantMsg, id: Date.now().toString(), created_at: new Date().toISOString() }]);
    } catch (err) {
      setError('Sorry, I encountered an error. Please try again or contact support.');
      console.error('Chat error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFeedback = async (messageId: string, helpful: boolean) => {
    await supabase
      .from('chat_messages')
      .update({ was_helpful: helpful })
      .eq('id', messageId);

    setMessages(prev =>
      prev.map(msg => (msg.id === messageId ? { ...msg, was_helpful: helpful } : msg))
    );
  };

  const handleSuggestedQuestion = (question: string) => {
    setInput(question);
  };

  return (
    <div className="w-96 h-[600px] bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 flex flex-col overflow-hidden">
      <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white p-4 flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg">Customer Support</h3>
          <p className="text-xs text-blue-100">We're here to help</p>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-white/20 rounded-full transition-colors"
          aria-label="Close chat"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && !isLoading && (
          <div className="space-y-3">
            <p className="text-gray-600 text-sm">Get started with a common question:</p>
            {suggestedQuestions.map((question, idx) => (
              <button
                key={idx}
                onClick={() => handleSuggestedQuestion(question)}
                className="w-full text-left p-3 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-700 transition-colors"
              >
                {question}
              </button>
            ))}
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                message.role === 'user'
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              <p className="text-sm whitespace-pre-wrap">{message.content}</p>

              {message.role === 'assistant' && (
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-200">
                  <span className="text-xs text-gray-500">Was this helpful?</span>
                  <button
                    onClick={() => handleFeedback(message.id, true)}
                    className={`p-1 rounded transition-colors ${
                      message.was_helpful === true
                        ? 'bg-green-100 text-green-600'
                        : 'hover:bg-gray-200 text-gray-400'
                    }`}
                    aria-label="Mark as helpful"
                  >
                    <ThumbsUp className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => handleFeedback(message.id, false)}
                    className={`p-1 rounded transition-colors ${
                      message.was_helpful === false
                        ? 'bg-red-100 text-red-600'
                        : 'hover:bg-gray-200 text-gray-400'
                    }`}
                    aria-label="Mark as not helpful"
                  >
                    <ThumbsDown className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-2xl px-4 py-3">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="p-4 border-t border-gray-200">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Type your question..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isLoading}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-full hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Send message"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2 text-center">
          Ask about features, pricing, or how to use FoodOps
        </p>
      </div>
    </div>
  );
}
